import React, { useEffect, useState, useRef } from 'react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import { useForm } from 'react-hook-form';
import Popover from 'react-bootstrap/Popover';
import Select from 'react-select';
import { Row, Col, FormGroup } from 'react-bootstrap';
import { capitalize, isEmpty } from 'lodash';
import { MultiSelect } from 'react-multi-select-component';
import { useTranslation } from 'react-i18next';
import CustomTooltip from '../../Tooltip/CustomTooltip';
import { CurrentYearItemRenderer } from '../CurrentYearDropdown';

const customStyles = {
  option: (provided) => ({
    ...provided,
    padding: '5px 10px',
  }),
  container: () => ({
    // none of react-select's styles are passed to <Control />
    flex: '1',
    minWidth: 200,
    // marginRight: 10,
  }),
  control: () => ({
    // none of react-select's styles are passed to <Control />
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    borderBottom: '1px solid #848484',
    minHeight: 38,
  }),
  singleValue: (provided, _state) => {
    const opacity = _state.isDisabled ? 0.5 : 1;
    const transition = 'opacity 300ms';

    return { ...provided, opacity, transition };
  },
  menu: (provided) => ({
    ...provided,
    margin: '-9px 0 0 0',
    border: '1px solid #848484',
    borderRadius: 0,
    padding: 0,
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: 0,
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#848484',
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    display: 'none',
  }),
};
function BtnMultiFilter({
  onApplyFilter,
  onResetFilter,
  filterList,
  filterOnChange,
  ClassName,
  onClick,
  isValueFromOption,
  catBasedOnPTData = false,
  ptCascading,
}) {
  const { register, handleSubmit, setValue } = useForm();

  const target = useRef(null);
  const [show, setShow] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState({});
  const [selectedFiltersData, setSelectedFiltersData] = useState({});
  const [isApply, setIsApply] = useState(false);
  const { t } = useTranslation('common');

  // Helpers to get selected ids for cascading
  const cascading = typeof ptCascading === 'boolean' ? ptCascading : !!catBasedOnPTData;
  const getIds = (arr) => (Array.isArray(arr) ? arr.map((x) => x?.value).filter(Boolean) : []);
  const selectedPTIds = getIds(selectedFilter?.productType);
  const selectedCat1Ids = getIds(selectedFilter?.category1);
  const selectedCat2Ids = getIds(selectedFilter?.category2);
  const selectedCat3Ids = getIds(selectedFilter?.category3);

  const resetFilter = async () => {
    setSelectedFilter({});
    setSelectedFiltersData({});
    filterList.forEach((el) => {
      setValue(el.name, null);
    });
    onResetFilter();
    setShow(!show);
    setIsApply(false);
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
    setShow(!show);
  };

  const applyFilter = async () => {
    onApplyFilter(selectedFiltersData);
  };

  const setFilterObj = ({ registerName, val }, selectedType) => {
    if (selectedType === 'multiSelect') {
      setIsApply(true);
      // If Product Type changes, clear all downstream categories only when cascading is ON
      if (registerName === 'productType' && cascading) {
        setSelectedFilter((prev) => ({
          ...prev,
          category1: [],
          category2: [],
          category3: [],
          category4: [],
        }));
        setValue('category1', []);
        setValue('category2', []);
        setValue('category3', []);
        setValue('category4', []);
      }
      if (registerName === 'category1' || registerName === 'category2') {
        setSelectedFilter((prev) => ({
          ...prev,
          category3: null,
        }));
        setValue('category3', null);
      }

      if (registerName === 'category1' || registerName === 'category2' || registerName === 'category3') {
        setSelectedFilter((prev) => ({
          ...prev,
          category4: null,
        }));
        setValue('category4', null);
      }

      if (registerName === 'brand') {
        const selectedBrandIds = val?.map((x) => x.value) || [];
        const currentSubBrands = selectedFilter.subBrand || [];

        const validSubBrands = currentSubBrands.filter((subBrand) => {
          const subBrandFilter = filterList.find((filter) => filter.name === 'subBrand');
          const subBrandData = subBrandFilter?.options?.find((sb) => sb.value === subBrand.value);
          return subBrandData && selectedBrandIds.includes(subBrandData.brandId);
        });
        setSelectedFilter((prev) => ({
          ...prev,
          subBrand: validSubBrands,
        }));
        setValue('subBrand', validSubBrands);
      }

      setSelectedFiltersData((prev) => {
        return { ...prev, [registerName]: val, fieldName: registerName };
      });
    }

    if (val && selectedType === 'select') {
      setIsApply(true);
      if (registerName === 'category1' || registerName === 'category2') {
        setSelectedFilter((prev) => ({
          ...prev,
          category3: null,
        }));
        setValue('category3', null);
      }

      setSelectedFiltersData((prev) => {
        return { ...prev, [registerName]: val.value, fieldName: registerName };
      });
    }
    if (val === null && selectedType === 'select') {
      setSelectedFiltersData((prev) => {
        return { ...prev, [registerName]: null, fieldName: registerName };
      });
      setSelectedFilter((prev) => ({
        ...prev,
        [`${registerName}`]: null,
      }));
      setValue(`${registerName}`, null);
    }
  };

  useEffect(() => {
    if (isValueFromOption) {
      const obj = JSON.parse(localStorage.getItem('supplierSuggestionFilter'));
      setSelectedFilter((prev) => ({
        ...prev,
        brand: obj?.brand,
        subBrand: obj?.subBrand,
        category1: obj?.category1,
        category2: obj?.category2,
        category3: obj?.category3,
        category4: obj?.category4,
        year: obj?.year,
        month: obj?.month,
        productType: obj?.productType,
        season: obj?.season,
        supplier: obj?.supplier,
        section: obj?.section ? { value: obj?.section, name: obj?.section, label: capitalize(obj?.section) } : undefined,
        status: obj?.status ? { value: obj?.status, name: obj?.status, label: capitalize(obj?.status) } : undefined,
        actionType: obj?.actionType ? { value: obj?.actionType, name: obj?.actionType, label: capitalize(obj?.actionType) } : undefined,
      }));
      let count = 0;
      for (const val in obj) {
        if (obj[val]?.length > 0) {
          count++;
        }
      }

      if (count > 0) {
        setIsApply(true);
      }
    }
  }, []);

  useEffect(() => {
    if (!isEmpty(selectedFilter)) {
      const newArray = Object.values(selectedFilter);
      const results = newArray.filter((element) => element !== null);
      if (isEmpty(results)) {
        resetFilter();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilter]);

  const wrapperRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShow(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [wrapperRef]);

  useEffect(() => {
    if (filterOnChange) {
      filterOnChange(selectedFiltersData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFiltersData]);

  return (
    <CustomTooltip placement="bottom" tooltipText={t('COMPONENET_FILTER.FILTER')}>
      <button type="button" onClick={handleClick} className={isApply ? `btn d-inline-flex pr-0 active ${ClassName}` : `btn d-inline-flex pr-0 ${ClassName}`}>
        <OverlayTrigger
          show={show}
          target={target.current}
          placement={t('BTN_FILTER.BUTTON')}
          onHide={() => setShow(false)}
          trigger="focus"
          overlay={
            <Popover className="custom-popover horizontal-view-multifilter">
              <Popover.Content ref={wrapperRef}>
                <Row className="form-row">
                  {filterList?.map(({ name, options, placeHolder, multiSelect }, i) => {
                    // Compute filtered options per field when cascading is enabled
                    let filteredOptions = options || [];
                    if (cascading) {
                      if (name === 'category1') {
                        if (selectedPTIds?.length) {
                          const hasMeta = (options || []).some(
                            (opt) =>
                              (Array.isArray(opt?.isUsedProductType) && opt.isUsedProductType.length) ||
                              (Array.isArray(opt?.productType) && opt.productType.length),
                          );
                          const base = options || [];
                          filteredOptions = hasMeta
                            ? base.filter((opt) => {
                                const used = Array.isArray(opt?.isUsedProductType) ? opt.isUsedProductType : [];
                                const pts = Array.isArray(opt?.productType)
                                  ? opt.productType.map((p) => (typeof p === 'object' ? p?._id : p)).filter(Boolean)
                                  : [];
                                return used?.some((id) => selectedPTIds.includes(id)) || pts?.some((id) => selectedPTIds.includes(id));
                              })
                            : base;
                        }
                      }
                      if (name === 'category2') {
                        if (selectedCat1Ids?.length) {
                          const hasMeta = (options || []).some((opt) => Array.isArray(opt?.isUsedCat1) && opt.isUsedCat1.length);
                          const base = options || [];
                          filteredOptions = hasMeta
                            ? base.filter((opt) => {
                                const usedCat1 = Array.isArray(opt?.isUsedCat1) ? opt.isUsedCat1 : [];
                                return usedCat1?.some((id) => selectedCat1Ids.includes(id));
                              })
                            : base;
                        }
                      }
                      if (name === 'category3') {
                        if (selectedCat1Ids?.length && selectedCat2Ids?.length) {
                          const hasMeta = (options || []).some((opt) => opt?.categoryOneId && opt?.categoryTwoId);
                          const base = options || [];
                          filteredOptions = hasMeta
                            ? base.filter((opt) => {
                                const c1 = opt?.categoryOneId;
                                const c2 = opt?.categoryTwoId;
                                return selectedCat1Ids.includes(c1) && selectedCat2Ids.includes(c2);
                              })
                            : base;
                        }
                      }
                      if (name === 'category4') {
                        if (selectedCat1Ids?.length && selectedCat2Ids?.length && selectedCat3Ids?.length) {
                          const hasMeta = (options || []).some((opt) => opt?.categoryOneId && opt?.categoryTwoId && opt?.categoryThreeId);
                          const base = options || [];
                          filteredOptions = hasMeta
                            ? base.filter((opt) => {
                                const c1 = opt?.categoryOneId;
                                const c2 = opt?.categoryTwoId;
                                const c3 = opt?.categoryThreeId;
                                return selectedCat1Ids.includes(c1) && selectedCat2Ids.includes(c2) && selectedCat3Ids.includes(c3);
                              })
                            : base;
                        }
                      }
                    }
                    const registerName = name;
                    return multiSelect ? (
                      <Col xs={6} md={4} key={i}>
                        <FormGroup className="flex-fill">
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            className="flex-fill"
                          >
                            <MultiSelect
                              name={registerName}
                              {...register(`${registerName}`, {
                                required: false,
                              })}
                              // disabled={}
                              overrideStrings={{
                                selectSomeItems: placeHolder,
                                // allItemsAreSelected: "All Items are Selected",
                                // selectAll: "Select Brand",
                                // search: "Search brand",
                              }}
                              options={filteredOptions}
                              ItemRenderer={name === 'year' ? CurrentYearItemRenderer : undefined}
                              className="product-custom-multiselect common-new-multiselect"
                              value={selectedFilter[registerName] || []}
                              onChange={(val) => {
                                setValue(registerName, val || []);
                                setSelectedFilter((prev) => ({
                                  ...prev,
                                  [registerName]: val || [],
                                }));
                                setFilterObj({ registerName, val }, 'multiSelect');
                              }}
                              labelledBy={placeHolder}
                            />
                          </div>
                        </FormGroup>
                      </Col>
                    ) : (
                      <Col xs={6} md={4} key={i}>
                        <FormGroup className="flex-fill ">
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            className="common-react-select mb-0 flex-fill"
                          >
                            <Select
                              name={registerName}
                              {...register(`${registerName}`, {
                                required: false,
                              })}
                              placeholder={placeHolder}
                              isClearable
                              options={options || []}
                              getOptionLabel={(option) => `${option?.label}`}
                              getOptionValue={(option) => `${option?.value}`}
                              value={selectedFilter[registerName] || undefined}
                              className="common-select"
                              classNamePrefix="common-select"
                              onChange={(val) => {
                                setValue(registerName, val);
                                setSelectedFilter((prev) => ({
                                  ...prev,
                                  [registerName]: val || null,
                                }));
                                setFilterObj({ registerName, val }, 'select');
                              }}
                              styles={customStyles}
                            />
                          </div>
                        </FormGroup>
                      </Col>
                    );
                  })}
                </Row>

                <div className="d-flex justify-content-end">
                  <CustomTooltip placement="top" tooltipText={t('COMPONENET_FILTER.CLEAR')}>
                    <button aria-label="Clear" type="button" onClick={() => resetFilter()} className="btn p-0 d-inline-flex" disabled={!isApply}>
                      <i className="icon-Clear-New f-24" />
                    </button>
                  </CustomTooltip>
                  <CustomTooltip placement="top" tooltipText={t('COMPONENET_FILTER.APPLY')}>
                    <button aria-label="Apply" type="button" onClick={handleSubmit(applyFilter)} className="btn p-0 d-inline-flex ml-16" disabled={!isApply}>
                      <i className="icon-Tick-1 f-28" />
                    </button>
                  </CustomTooltip>
                </div>
              </Popover.Content>
            </Popover>
          }
        >
          <i className={isApply ? 'icon-Filter font-weight-heavy f-26' : 'icon-Filter f-26'} />
        </OverlayTrigger>
        {/* <span className="text-icon">{t("BTN_FILTER.GROUP")}</span> */}
      </button>
    </CustomTooltip>
  );
}

export default BtnMultiFilter;
